import axios from "axios";
import { BASE_URL, REFRESH_TOKEN_URL } from "./config";
import { showToast } from "../common/Toast";

const Api = axios.create({
  baseURL: BASE_URL,
  timeout: 10000,
});

const refreshClient = axios.create({
  baseURL: BASE_URL,
  timeout: 10000,
});

const extractErrorMessage = (error) => {
  if (!error) return "Unknown error";

  if (error.message === "Network Error") {
    return "Network Error";
  }

  if (error.code === "ECONNABORTED") {
    return "Request timeout — server took too long to respond.";
  }

  return (
    error.response?.data?.message ||
    error.response?.message ||
    error.message ||
    "Something went wrong"
  );
};

Api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("auth_token");
    if (token) {
      config.headers.Authorization = `${token}`;
    }
    return config;
  },
  (error) => error.response.data
);

let isLoggingOut = false;
let isRefreshing = false;
let pendingRequests = [];

const clearAuthAndRedirect = () => {
  if (isLoggingOut) return;
  isLoggingOut = true;

  showToast.error("Session expired. Please login again.");

  setTimeout(() => {
    localStorage.removeItem("auth_token");
    localStorage.removeItem("refresh_token");
    window.location.href = "/";
  }, 1000);
};

const subscribeTokenRefresh = (resolve, reject, requestConfig) => {
  pendingRequests.push({ resolve, reject, requestConfig });
};

const onRefreshed = (newToken) => {
  pendingRequests.forEach(({ resolve, requestConfig }) => {
    requestConfig.headers = requestConfig.headers || {};
    requestConfig.headers.Authorization = `${newToken}`;
    resolve(Api(requestConfig));
  });
  pendingRequests = [];
};

const onRefreshFailed = (refreshError) => {
  pendingRequests.forEach(({ reject }) => reject(refreshError));
  pendingRequests = [];
};

const requestNewAccessToken = async () => {
  const refreshToken = localStorage.getItem("refresh_token");
  if (!refreshToken) {
    throw new Error("Missing refresh token");
  }

  const response = await refreshClient.post(
    REFRESH_TOKEN_URL,
    { refreshToken },
    {
      headers: {
        Authorization: `${refreshToken}`,
      },
    }
  );

  const data = response?.data || {};
  const newAccessToken = data.token || data.accessToken;
  const newRefreshToken = data.refreshToken;

  if (!newAccessToken) {
    throw new Error("Invalid refresh token response");
  }

  localStorage.setItem("auth_token", newAccessToken);
  if (newRefreshToken) {
    localStorage.setItem("refresh_token", newRefreshToken);
  }

  return newAccessToken;
};

Api.interceptors.response.use(
  (response) => response.data,
  async (error) => {
    const originalRequest = error?.config;
    const isRefreshCall = originalRequest?.url?.includes(REFRESH_TOKEN_URL);

    if (
      error.response?.status === 401 &&
      originalRequest &&
      !originalRequest._retry &&
      !isRefreshCall
    ) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          subscribeTokenRefresh(resolve, reject, originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const newToken = await requestNewAccessToken();
        onRefreshed(newToken);
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `${newToken}`;
        return Api(originalRequest);
      } catch (refreshError) {
        onRefreshFailed(refreshError);
        clearAuthAndRedirect();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    const message = extractErrorMessage(error);

    if (error.response?.status === 401) {
      clearAuthAndRedirect();
      return Promise.reject(error);
    }

    showToast.error(message);
    return Promise.reject(error);
  } 
);

export default Api;
